import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardPath, DASHBOARD_PATHS } from "../../config/dashboardRoutes.js";
import { Popover } from "react-tiny-popover";
import NavBar from "../../component/navBar/NavBar";
import Title from "../../component/title/Title";
import Dropdown from "../../component/dropdown/Dropdown";
import CustomDatePicker from "../../component/datePicker/DatePicker";
import LoadingSpinner from "../../component/loading/LoadingSpinner";
import { PaginationButton } from "../../component/buttonComponent/ButtonComponent";
import Invalid from "../../component/textInput/img/invalid.svg";
import { useAuth } from "../../config/utilities/useAuth";
import { useTimezone } from "../../config/utilities/useTimezone";
import { APIGetChilds } from "../../config/utilities/APIs/childApi";
import { APIGetFamilySchedules } from "../../config/utilities/APIs/schedulingApi";
import {
  formatToYYYYMMDD,
  formatWithOrdinal,
  formatDateTime,
} from "../../config/utilities/fDate";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import "./Scheduling.css";
import "./StudentSchedules.css";

const TRIP_TYPE_COLORS = [
  { background: "#ECFDFF", color: "#06AED4" },
  { background: "#FEF6EE", color: "#B93815" },
  { background: "#F4F3FF", color: "#5925DC" },
  { background: "#E0F2FE", color: "#0C4A6E" },
  { background: "#FFFBEA", color: "#92400E" },
];

// Preferred display order for trip sessions; anything else falls after these
// and is ordered alphabetically.
const SESSION_ORDER = ["none", "am", "pm", "eca"];

const PREFERENCE_DAYS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
];

// Order trip-type codes by the preferred session order, then alphabetically.
const orderSessionCodes = (codes) =>
  [...codes].sort((a, b) => {
    const al = String(a).toLowerCase();
    const bl = String(b).toLowerCase();
    const ai = SESSION_ORDER.indexOf(al);
    const bi = SESSION_ORDER.indexOf(bl);
    const aRank = ai === -1 ? SESSION_ORDER.length : ai;
    const bRank = bi === -1 ? SESSION_ORDER.length : bi;
    if (aRank !== bRank) return aRank - bRank;
    return al.localeCompare(bl);
  });

// Per-child attendance status styling (matches the rest of the app).
const CHILD_STATUS_MAP = {
  pickup: { label: "Picked Up", background: "#EFF8FF", color: "#175CD3" },
  dropoff: { label: "Dropped Off", background: "#ECFDF3", color: "#027A48" },
  failedtoboard: { label: "Failed to Board", background: "#FEF3F2", color: "#B42318" },
  scheduled: { label: "Scheduled", background: "#FFFAEB", color: "#B54708" },
  exempted: { label: "Exempted", background: "#F8F8FC", color: "#363F72" },
};

// Fixed, selectable attendance statuses for the schedule filter, ordered to
// follow the per-child attendance lifecycle.
const STATUS_FILTER_OPTIONS = [
  "scheduled",
  "pickup",
  "dropoff",
  "failedtoboard",
  "exempted",
];

const getDefaultDateRange = () => {
  const start = new Date();
  const end = new Date();
  end.setDate(start.getDate() + 6);
  return {
    startDate: formatToYYYYMMDD(start),
    endDate: formatToYYYYMMDD(end),
  };
};

const SCHEDULE_TRIPS_PER_PAGE = 10;

const DEFAULT_EXPANDED_SECTIONS = {
  info: true,
  stops: true,
  preferences: true,
  schedule: true,
};

export default function StudentSchedules() {
  const { privilege, tripTypes } = useAuth();
  const { timezone } = useTimezone();
  const institutionId = privilege?.institutionId;

  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [dateRange, setDateRange] = useState(getDefaultDateRange);

  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [familySchedules, setFamilySchedules] = useState([]);
  const [tripTypeFilter, setTripTypeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [filtersPopoverOpen, setFiltersPopoverOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(DEFAULT_EXPANDED_SECTIONS);
  const [schedulePage, setSchedulePage] = useState(1);

  const normalizeStatus = (status) =>
    String(status || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");

  const toggleFilter = (setter, value) =>
    setter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );

  const getSessionLabel = (session) => {
    if (!session || session === "none") return "All Trip Sessions";
    const match = tripTypes?.find((type) => type.id === session);
    return match?.name || session;
  };

  const getSessionColors = (session) => {
    if (!session || session === "none") {
      return { background: "#F2F4F7", color: "#344054" };
    }
    const index = tripTypes?.findIndex((type) => type.id === session);
    return TRIP_TYPE_COLORS[index] || { background: "#F2F4F7", color: "#344054" };
  };

  const formatStudent = (student) => {
    const stopList = (student.stopData || [])
      .map((stop) => ({
        name: stop.stopName || "Unknown Stop",
        session: stop.tripSession || "none",
        sessionLabel: getSessionLabel(stop.tripSession),
      }))
      .sort((a, b) => {
        const aSession = String(a.session).toLowerCase();
        const bSession = String(b.session).toLowerCase();
        const aIndex = SESSION_ORDER.indexOf(aSession);
        const bIndex = SESSION_ORDER.indexOf(bSession);
        const aRank = aIndex === -1 ? SESSION_ORDER.length : aIndex;
        const bRank = bIndex === -1 ? SESSION_ORDER.length : bIndex;

        if (aRank !== bRank) return aRank - bRank;
        // Both sessions fall outside the preferred list: order alphabetically.
        if (aRank === SESSION_ORDER.length && aSession !== bSession) {
          return aSession.localeCompare(bSession);
        }
        // Same session: order stops alphabetically by name.
        return a.name.localeCompare(b.name);
      });

    return {
      id: student._id,
      familyId: student.familyId || student.family?._id || "",
      name: `${student.firstName || ""} ${student.lastName || ""}`.trim(),
      class: student.class || "—",
      grade: student.grade || "",
      stopList,
      preferences: student.preferences || null,
    };
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const response = await APIGetChilds(setLoading, institutionId);

      if (response?.status === 200) {
        const rawStudents = response.data?.data?.childs || [];
        setStudents(rawStudents.map(formatStudent));
      } else {
        console.warn("Unexpected response fetching childs:", response);
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching childs:", error);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!institutionId) return;
    fetchStudents();
  }, [institutionId]);

  const studentOptions = useMemo(
    () =>
      students
        .map((student) => ({
          label:
            student.class && student.class !== "—"
              ? `${student.name} • ${student.class}`
              : student.name,
          value: student.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [students]
  );

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === selectedStudentId) || null,
    [students, selectedStudentId]
  );

  // Same rule as StopForm: a student cannot belong to two stops with the same
  // trip session (including "none" / All Trip Sessions).
  const stopSessionConflicts = useMemo(() => {
    const stopList = selectedStudent?.stopList || [];
    if (stopList.length < 2) return [];

    const bySession = stopList.reduce((acc, stop) => {
      const session = stop.session || "none";
      if (!acc[session]) acc[session] = [];
      acc[session].push(stop);
      return acc;
    }, {});

    return Object.entries(bySession)
      .filter(([, stops]) => stops.length > 1)
      .map(([session, stops]) => ({
        session,
        sessionLabel: getSessionLabel(session),
        stops,
      }));
  }, [selectedStudent?.stopList, tripTypes]);

  const fetchFamilySchedules = async (familyId, range) => {
    if (!familyId || !range?.startDate || !range?.endDate) {
      setFamilySchedules([]);
      return;
    }

    setScheduleLoading(true);
    try {
      const response = await APIGetFamilySchedules({
        familyId,
        startDate: range.startDate,
        endDate: range.endDate,
        institutionId,
      });

      if (response?.status === 200) {
        const data = response.data?.data ?? response.data;
        // The endpoint may return the list under a few different keys; pick
        // whichever array is present so the UI keeps working as the shape firms up.
        const schedules = Array.isArray(data)
          ? data
          : data?.schedules ||
            data?.familySchedules ||
            data?.familySchedule ||
            [];
        setFamilySchedules(Array.isArray(schedules) ? schedules : []);
      } else {
        console.warn("Unexpected response fetching family schedules:", response);
        setFamilySchedules([]);
      }
    } catch (error) {
      console.error("Error fetching family schedules:", error);
      setFamilySchedules([]);
    } finally {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedStudent?.familyId) {
      setFamilySchedules([]);
      return;
    }
    fetchFamilySchedules(selectedStudent.familyId, dateRange);
  }, [selectedStudent?.familyId, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    setSchedulePage(1);
  }, [
    selectedStudentId,
    dateRange.startDate,
    dateRange.endDate,
    tripTypeFilter,
    statusFilter,
  ]);

  const handleChange = (e) => setSelectedStudentId(e.target.value);

  const toggleSection = (sectionKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const renderCollapsibleCard = (
    sectionKey,
    title,
    content,
    headerExtra = null,
    options = {}
  ) => {
    const { fixedHeight = false } = options;
    const isExpanded = expandedSections[sectionKey];

    return (
      <section
        className={`student-schedule-card${
          isExpanded ? "" : " student-schedule-card-collapsed"
        }`}
      >
        <div
          className={`student-schedule-card-header-row${
            headerExtra ? " student-schedule-card-header-row-split" : ""
          }`}
        >
          <button
            type="button"
            className="student-schedule-card-toggle"
            onClick={() => toggleSection(sectionKey)}
            aria-expanded={isExpanded}
          >
            <span
              className={`student-schedule-card-chevron${
                isExpanded ? " is-expanded" : ""
              }`}
              aria-hidden="true"
            >
              ›
            </span>
            <h3 className="student-schedule-card-title">{title}</h3>
          </button>
          {headerExtra && isExpanded ? headerExtra : null}
        </div>
        {isExpanded ? (
          <div
            className={`student-schedule-card-body${
              fixedHeight ? " student-schedule-card-body-fixed" : ""
            }`}
          >
            {content}
          </div>
        ) : null}
      </section>
    );
  };

  const renderEmptyIcon = (type) => {
    switch (type) {
      case "calendar-filtered":
        return (
          <svg viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M8 2v4M16 2v4M3 10h18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        );
      case "calendar":
        return (
          <svg viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M8 2v4M16 2v4M3 10h18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <path
              d="M8 14h2v2H8v-2Zm4 0h2v2h-2v-2Zm4 0h2v2h-2v-2Z"
              fill="currentColor"
            />
          </svg>
        );
      case "stops":
        return (
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="11" r="2.25" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        );
      case "preferences":
        return (
          <svg viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="5"
              width="18"
              height="14"
              rx="2"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <path
              d="M3 10h18M8 5V3M16 5V3M8 14h2v2H8v-2Zm4 0h2v2h-2v-2Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderCardEmptyState = ({
    icon,
    title,
    text,
    hint,
    action,
    compact = false,
  }) => (
    <div
      className={`student-schedule-empty-state${
        compact ? " student-schedule-empty-state-compact" : ""
      }`}
    >
      <div className="student-schedule-empty-state-icon" aria-hidden="true">
        {renderEmptyIcon(icon)}
      </div>
      <h4 className="student-schedule-empty-state-title">{title}</h4>
      <p className="student-schedule-empty-state-text">{text}</p>
      {hint ? (
        <p className="student-schedule-empty-state-hint">{hint}</p>
      ) : null}
      {action}
    </div>
  );

  const renderScheduleEmptyState = (variant) => {
    const isFiltered = variant === "filtered";

    return renderCardEmptyState({
      icon: isFiltered ? "calendar-filtered" : "calendar",
      title: isFiltered ? "No matching schedules" : "No schedules in this range",
      text: isFiltered
        ? "Nothing matches your current trip type or status filters. Try clearing filters or choosing a wider date range."
        : `No trips were found for ${selectedStudent.name} between ${formatScheduleDate(dateRange.startDate)} and ${formatScheduleDate(dateRange.endDate)}.`,
      hint: isFiltered
        ? undefined
        : "Adjust the From and To dates above to search a different period.",
      action: isFiltered ? (
        <button
          type="button"
          className="student-schedule-empty-state-action"
          onClick={() => {
            setTripTypeFilter([]);
            setStatusFilter([]);
          }}
        >
          Clear filters
        </button>
      ) : undefined,
    });
  };

  const formatScheduleDate = (value) => {
    if (!value || value === "—") return "—";
    const ymdMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = ymdMatch
      ? new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]))
      : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return formatWithOrdinal(date);
  };

  const formatTripType = (code) => {
    if (!code) return "—";
    const match = tripTypes?.find((type) => type.id === code);
    return match?.name || code.toUpperCase();
  };

  const formatChildStatus = (status) => {
    const key = String(status || "").toLowerCase().replace(/[\s_-]/g, "");
    return (
      CHILD_STATUS_MAP[key] || {
        label: formatCapitalize(status) || "—",
        background: "#F2F4F7",
        color: "#344054",
      }
    );
  };

  // ISO timestamps from the API are UTC; render them in the institution timezone.
  const formatIsoTime = (iso) => {
    if (!iso) return "—";
    return formatDateTime(iso, timezone).formattedTime || "—";
  };

  const renderStudentInfo = () =>
    renderCollapsibleCard(
      "info",
      "Student Information",
      <div className="student-schedule-info-grid">
        <div className="student-schedule-info-item">
          <span className="student-schedule-info-label">Full Name</span>
          <span className="student-schedule-info-value">
            {selectedStudent.name || "—"}
          </span>
        </div>
        <div className="student-schedule-info-item">
          <span className="student-schedule-info-label">Class</span>
          <span className="student-schedule-info-value">
            {selectedStudent.class || "—"}
          </span>
        </div>
        <div className="student-schedule-info-item">
          <span className="student-schedule-info-label">Grade</span>
          <span className="student-schedule-info-value">
            {selectedStudent.grade
              ? formatCapitalize(selectedStudent.grade)
              : "—"}
          </span>
        </div>
        <div className="student-schedule-info-item">
          <span className="student-schedule-info-label">Total Stops</span>
          <span className="student-schedule-info-value">
            {selectedStudent.stopList?.length || 0}
          </span>
        </div>
      </div>
    );

  const renderStopSessionWarning = () => {
    if (!stopSessionConflicts.length) return null;

    return (
      <div className="student-schedule-stop-conflict" role="alert">
        <div className="student-schedule-stop-conflict-main">
          <img
            src={Invalid}
            alt=""
            className="student-schedule-stop-conflict-icon"
          />
          <div className="student-schedule-stop-conflict-text">
            <p className="student-schedule-stop-conflict-message">
              This student is assigned to multiple stops with the same Trip
              Session. A student cannot belong to two stops with the same Trip
              Session. Please go to the{" "}
              <Link
                to={dashboardPath(DASHBOARD_PATHS.stops)}
                className="student-schedule-stop-conflict-link"
              >
                Stops page
              </Link>{" "}
              to update their Trip Session and resolve the conflict.
            </p>
          </div>
        </div>
        <ul className="student-schedule-stop-conflict-list">
          {stopSessionConflicts.map((conflict) => (
            <li key={conflict.session}>
              <strong>{conflict.sessionLabel}</strong> is handled by{" "}
              {conflict.stops.map((stop) => stop.name).join(", ")}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderAssociatedStops = () =>
    renderCollapsibleCard(
      "stops",
      "Associated Stops",
      selectedStudent.stopList?.length ? (
        <div className="student-schedule-stop-list">
          {selectedStudent.stopList.map((stop, index) => {
            const colors = getSessionColors(stop.session);
            return (
              <div className="student-schedule-stop-row" key={index}>
                <span className="student-schedule-stop-name">{stop.name}</span>
                <span
                  className="student-schedule-session-badge"
                  style={{ background: colors.background, color: colors.color }}
                >
                  {stop.sessionLabel}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        renderCardEmptyState({
          icon: "stops",
          title: "No associated stops",
          text: `${selectedStudent.name} is not linked to any bus stops yet.`,
          hint: "Assign this student to a stop and trip session from the Stops page.",
          compact: true,
          action: (
            <Link
              to={dashboardPath(DASHBOARD_PATHS.stops)}
              className="student-schedule-empty-state-action"
            >
              Go to Stops
            </Link>
          ),
        })
      )
    );

  const renderPreferences = () => {
    const preferences = selectedStudent.preferences;
    const columns = preferences
      ? orderSessionCodes(
          PREFERENCE_DAYS.reduce((set, day) => {
            Object.keys(preferences[day.key] || {}).forEach((code) =>
              set.add(code)
            );
            return set;
          }, new Set())
        )
      : [];

    return renderCollapsibleCard(
      "preferences",
      "Default Preferences",
      columns.length ? (
        <div className="student-preferences-table-wrap">
          <table className="student-preferences-table">
            <thead>
              <tr>
                <th>Day</th>
                {columns.map((code) => (
                  <th key={code}>{formatTripType(code)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PREFERENCE_DAYS.map((day) => (
                <tr key={day.key}>
                  <td className="student-preferences-day">{day.label}</td>
                  {columns.map((code) => {
                    const active = Boolean(preferences[day.key]?.[code]);
                    return (
                      <td key={code}>
                        <span
                          className={`student-preferences-cell ${
                            active
                              ? "student-preferences-cell-on"
                              : "student-preferences-cell-off"
                          }`}
                        >
                          {active ? "✓" : "–"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        renderCardEmptyState({
          icon: "preferences",
          title: "No default preferences",
          text: `${selectedStudent.name} does not have default weekday trip preferences set.`,
          hint: "Default preferences define which trip types apply on each school day.",
          compact: true,
        })
      )
    );
  };

  const renderTripCard = (entry, index) => {
    const { schedule, child } = entry;

    const tripColors = getSessionColors(schedule.tripType);
    const statusInfo = formatChildStatus(child.status);
    const isExempted = normalizeStatus(child.status) === "exempted";
    const vehicleNumber = schedule.vehicleDetails?.vehicleNumber || "";
    const busNumber = schedule.vehicleDetails?.busNumber || "";
    const vehicleLabel = vehicleNumber
      ? busNumber
        ? `${vehicleNumber} (${busNumber})`
        : vehicleNumber
      : "—";

    const detailRows = [
      { label: "Stop", value: child.stopName || "—" },
      { label: "Vehicle", value: vehicleLabel },
    ];

    const timeRows = [
      {
        label: "Pickup",
        scheduled: formatIsoTime(child.scheduledPickupTime),
        actual: formatIsoTime(child.actualPickupTime),
      },
      {
        label: "Dropoff",
        scheduled: formatIsoTime(child.scheduledDropoffTime),
        actual: formatIsoTime(child.actualDropoffTime),
      },
    ];

    return (
      <div className="student-schedule-trip-item" key={schedule._id || index}>
        <div className="student-schedule-trip-main">
          <div className="student-schedule-trip-name-group">
            <span className="student-schedule-trip-name">
              {schedule.startStop || "—"} → {schedule.endStop || "—"}
            </span>
            <span
              className="student-schedule-session-badge"
              style={{
                background: tripColors.background,
                color: tripColors.color,
              }}
            >
              {formatTripType(schedule.tripType)}
            </span>
          </div>
          <div className="student-schedule-trip-badges">
            <span
              className="student-schedule-session-badge"
              style={{
                background: statusInfo.background,
                color: statusInfo.color,
              }}
            >
              {statusInfo.label}
            </span>
          </div>
        </div>
        <div className="student-schedule-trip-meta">
          {detailRows.map((row) => (
            <span key={row.label} className="student-schedule-trip-meta-item">
              <span className="student-schedule-trip-meta-label">
                {row.label}:
              </span>{" "}
              {row.value}
            </span>
          ))}
        </div>
        {!isExempted && (
        <div className="student-schedule-trip-times">
          {timeRows.map((row) => (
            <div className="student-schedule-time-block" key={row.label}>
              <span className="student-schedule-time-block-title">
                {row.label}
              </span>
              <div className="student-schedule-time-block-rows">
                <div className="student-schedule-time-block-row">
                  <span className="student-schedule-time-dot student-schedule-time-dot-scheduled" />
                  <span className="student-schedule-time-block-label">
                    Scheduled
                  </span>
                  <span className="student-schedule-time-block-value">
                    {row.scheduled}
                  </span>
                </div>
                <div className="student-schedule-time-block-row">
                  <span className="student-schedule-time-dot student-schedule-time-dot-actual" />
                  <span className="student-schedule-time-block-label">
                    Actual
                  </span>
                  <span className="student-schedule-time-block-value">
                    {row.actual}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        )}
      </div>
    );
  };

  const renderSchedules = () => {
    // Although the endpoint is family-scoped, only surface the schedules that
    // include the selected child, and show that child's details only.
    const allChildTrips = familySchedules
      .map((schedule) => {
        const child = (schedule.childs || []).find(
          (entry) => entry.childId === selectedStudent.id
        );
        return child ? { schedule, child } : null;
      })
      .filter(Boolean);

    // Trip types are defined per institution and statuses are a fixed set, so
    // the filter options stay stable regardless of what the current range
    // happens to contain.
    const availableTripTypes = (tripTypes || []).map((type) => type.id);
    const availableStatuses = STATUS_FILTER_OPTIONS;

    const filtersActive = tripTypeFilter.length > 0 || statusFilter.length > 0;
    const activeFilterCount = tripTypeFilter.length + statusFilter.length;

    const childTrips = allChildTrips.filter((entry) => {
      const tripOk =
        !tripTypeFilter.length ||
        tripTypeFilter.includes(entry.schedule.tripType);
      const statusOk =
        !statusFilter.length ||
        statusFilter.includes(normalizeStatus(entry.child.status));
      return tripOk && statusOk;
    });

    // Keep the selectable window capped at one month in either direction.
    const shiftMonths = (dateStr, months) => {
      if (!dateStr) return undefined;
      const ymdMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      const d = ymdMatch
        ? new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]))
        : new Date(dateStr);
      const targetMonth = d.getMonth() + months;
      const anchorDay = d.getDate();
      d.setDate(1);
      d.setMonth(targetMonth);
      const daysInMonth = new Date(
        d.getFullYear(),
        d.getMonth() + 1,
        0
      ).getDate();
      d.setDate(Math.min(anchorDay, daysInMonth));
      return formatToYYYYMMDD(d);
    };
    const maxToDate = shiftMonths(dateRange.startDate, 1);
    const minFromDate = shiftMonths(dateRange.endDate, -1);

    const filtersAvailable =
      availableTripTypes.length > 0 || availableStatuses.length > 0;

    const filterPopoverContent = (
      <div className="student-schedule-filters-popover">
        <div className="student-schedule-filters-popover-header">
          <span className="student-schedule-filters-popover-title">
            Filters
          </span>
          {filtersActive && (
            <button
              type="button"
              className="student-schedule-filters-popover-clear"
              onClick={() => {
                setTripTypeFilter([]);
                setStatusFilter([]);
              }}
            >
              Clear all
            </button>
          )}
        </div>

        {availableTripTypes.length > 0 && (
          <div className="student-schedule-filters-popover-section">
            <span className="student-schedule-filters-popover-section-title">
              Trip types
            </span>
            <div className="student-schedule-filter-pills">
              <button
                type="button"
                className={`student-schedule-filter-pill student-schedule-filter-pill-all${
                  tripTypeFilter.length === 0 ? " is-active" : ""
                }`}
                onClick={() => setTripTypeFilter([])}
              >
                All types
              </button>
              {availableTripTypes.map((code) => {
                const active = tripTypeFilter.includes(code);
                const colors = getSessionColors(code);
                return (
                  <button
                    key={code}
                    type="button"
                    className={`student-schedule-filter-pill${
                      active ? " is-active" : ""
                    }`}
                    style={
                      active
                        ? {
                            background: colors.background,
                            color: colors.color,
                            borderColor: colors.color,
                          }
                        : undefined
                    }
                    onClick={() => toggleFilter(setTripTypeFilter, code)}
                  >
                    {formatTripType(code)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {availableStatuses.length > 0 && (
          <div className="student-schedule-filters-popover-section">
            <span className="student-schedule-filters-popover-section-title">
              Status
            </span>
            <div className="student-schedule-filter-pills">
              <button
                type="button"
                className={`student-schedule-filter-pill student-schedule-filter-pill-all${
                  statusFilter.length === 0 ? " is-active" : ""
                }`}
                onClick={() => setStatusFilter([])}
              >
                All statuses
              </button>
              {availableStatuses.map((key) => {
                const active = statusFilter.includes(key);
                const info = formatChildStatus(key);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`student-schedule-filter-pill${
                      active ? " is-active" : ""
                    }`}
                    style={
                      active
                        ? {
                            background: info.background,
                            color: info.color,
                            borderColor: info.color,
                          }
                        : undefined
                    }
                    onClick={() => toggleFilter(setStatusFilter, key)}
                  >
                    {info.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );

    const sortedTrips = [...childTrips].sort((a, b) => {
      const dateA = a.schedule.scheduleDate || "";
      const dateB = b.schedule.scheduleDate || "";
      return dateA.localeCompare(dateB);
    });

    const totalSchedulePages = Math.max(
      1,
      Math.ceil(sortedTrips.length / SCHEDULE_TRIPS_PER_PAGE)
    );
    const safeSchedulePage = Math.min(schedulePage, totalSchedulePages);
    const paginatedTrips = sortedTrips.slice(
      (safeSchedulePage - 1) * SCHEDULE_TRIPS_PER_PAGE,
      safeSchedulePage * SCHEDULE_TRIPS_PER_PAGE
    );

    const paginatedGrouped = paginatedTrips.reduce((acc, entry) => {
      const dateKey = entry.schedule.scheduleDate || "—";
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(entry);
      return acc;
    }, {});

    const paginatedDates = Object.keys(paginatedGrouped).sort();

    const scheduleControls = (
      <div className="student-schedule-card-controls">
        {!scheduleLoading && filtersAvailable && (
          <Popover
            isOpen={filtersPopoverOpen}
            positions={["bottom", "left", "top"]}
            align="start"
            padding={8}
            onClickOutside={() => setFiltersPopoverOpen(false)}
            containerStyle={{ zIndex: 1000 }}
            content={filterPopoverContent}
          >
            <button
              type="button"
              className={`student-schedule-filters-trigger${
                filtersActive ? " has-active-filters" : ""
              }${filtersPopoverOpen ? " is-open" : ""}`}
              onClick={() => setFiltersPopoverOpen((open) => !open)}
              aria-expanded={filtersPopoverOpen}
              aria-haspopup="dialog"
            >
              <span className="student-schedule-filters-trigger-label">
                Filters
              </span>
              {filtersActive && (
                <span
                  className="student-schedule-filters-badge"
                  aria-hidden="true"
                >
                  {activeFilterCount}
                </span>
              )}
            </button>
          </Popover>
        )}
        <div className="student-schedule-date-range">
          <CustomDatePicker
            label="From"
            name="startDate"
            input={dateRange}
            setInput={setDateRange}
            setError={() => {}}
            minDate={minFromDate}
            maxDate={dateRange.endDate}
            width="11rem"
            placeholder="Start date"
          />
          <CustomDatePicker
            label="To"
            name="endDate"
            input={dateRange}
            setInput={setDateRange}
            setError={() => {}}
            minDate={dateRange.startDate}
            maxDate={maxToDate}
            width="11rem"
            placeholder="End date"
          />
        </div>
      </div>
    );

    const schedulePagination =
      !scheduleLoading && sortedTrips.length > SCHEDULE_TRIPS_PER_PAGE ? (
        <div className="student-schedule-pagination">
          <span className="student-schedule-pagination-total">
            {sortedTrips.length} trips total
          </span>
          <span className="student-schedule-pagination-info">
            Page {safeSchedulePage} of {totalSchedulePages}
          </span>
          <div className="student-schedule-pagination-buttons">
            <PaginationButton
              onClick={() => setSchedulePage((page) => Math.max(1, page - 1))}
              disabled={safeSchedulePage === 1}
              direction="previous"
            />
            <PaginationButton
              onClick={() =>
                setSchedulePage((page) =>
                  Math.min(totalSchedulePages, page + 1)
                )
              }
              disabled={safeSchedulePage === totalSchedulePages}
              direction="next"
            />
          </div>
        </div>
      ) : null;

    const scheduleScrollContent = scheduleLoading ? (
      <div className="student-scope-loading student-scope-loading-inline">
        <LoadingSpinner size="medium" color="#ffb502" />
      </div>
    ) : sortedTrips.length ? (
      <div className="student-schedule-day-list">
        {paginatedDates.map((dateKey) => (
          <div className="student-schedule-day-group" key={dateKey}>
            <div className="student-schedule-day-label">
              {formatScheduleDate(dateKey)}
            </div>
            <div className="student-schedule-trip-list">
              {paginatedGrouped[dateKey].map((entry, index) =>
                renderTripCard(entry, index)
              )}
            </div>
          </div>
        ))}
      </div>
    ) : filtersActive && allChildTrips.length > 0 ? (
      renderScheduleEmptyState("filtered")
    ) : (
      renderScheduleEmptyState("range")
    );

    const scheduleContent = (
      <>
        <div className="student-schedule-scroll">{scheduleScrollContent}</div>
        {schedulePagination}
      </>
    );

    return renderCollapsibleCard(
      "schedule",
      "Schedule",
      scheduleContent,
      scheduleControls,
      { fixedHeight: true }
    );
  };

  const renderStudentDetail = () => {
    if (!selectedStudent) return null;

    return (
      <div className="student-schedule-detail">
        {renderStudentInfo()}
        {renderStopSessionWarning()}
        <div className="student-schedule-two-col">
          {renderAssociatedStops()}
          {renderPreferences()}
        </div>
        {renderSchedules()}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="student-scope-empty">
      <div className="student-scope-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="m20 20-3.2-3.2"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <h3>Search for a student</h3>
      <p>
        Use the search above to find a student and view their schedule and
        associated stops.
      </p>
    </div>
  );

  return (
    <div className="main-page scheduling-page student-schedules-page">
      <NavBar type="studentSchedules" />
      <div className="content-area">
        <div className="title-container mobile-hide-page-title">
          <Title
            title="Student Schedules"
            subtitle="Search a student to view their schedule and associated stops"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        </div>
        <div className="main-content-container scheduling-main-content-container">
          <div className="student-scope-bar">
            <div className="student-scope-bar-inner">
              <div className="student-scope-bar-label">
                <span className="student-scope-bar-eyebrow">Student lookup</span>
                <span className="student-scope-bar-hint">
                  Search and select a student to see their schedule details.
                </span>
              </div>
              <div className="student-scope-bar-control">
                <Dropdown
                  placeholder={
                    loading ? "Loading students..." : "Search student..."
                  }
                  options={studentOptions}
                  name="student"
                  onChangeFunction={handleChange}
                  input={{ student: selectedStudentId }}
                  disabled={loading}
                />
              </div>
              <div className="student-scope-stats">
                <div className="student-scope-stat">
                  <span className="student-scope-stat-value">
                    {students.length}
                  </span>
                  <span className="student-scope-stat-label">Students</span>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="student-scope-loading">
              <LoadingSpinner size="large" color="#ffb502" />
            </div>
          ) : selectedStudent ? (
            renderStudentDetail()
          ) : (
            renderEmptyState()
          )}
        </div>
      </div>
    </div>
  );
}
